/**
 * contract.ts — the contract-shaped reads the setup page makes.
 *
 * MOVED VERBATIM out of `useContractSetup`. Same keys, same selects, same shapes: the point
 * of the move is that a fetcher registered under a key has exactly one home, so a second
 * surface that wants the same row imports this instead of writing a second query function
 * against the same key (which is how `['insurance-details']` ended up with two shapes and a
 * failing loan insert). Nothing here is new behaviour.
 */

import { supabase } from '@/integrations/supabase/client';

export interface PreparationItem {
    item: string;
    is_complete: boolean;
    details: any;
}

export const contractSetupKey = (contractId: number) => ['contract-setup', contractId] as const;
export const prepStatusKey = (contractId: number) => ['prep-status', contractId] as const;
export const contractStatusForSigningKey = (contractId: number) =>
    ['contract-status-for-signing', contractId] as const;
export const contractSetupStatusKey = (contractId: number) =>
    ['contract-setup-status', contractId] as const;

// ─── get_contract_setup_status — the server's own readiness answer ────────────

/**
 * The six section keys, listed in THE SERVER'S order — which is not the rail's.
 *
 * THE CLIENT KEYS INTO THIS PAYLOAD BY NAME AND NEVER BY POSITION (`AREA_SECTIONS` in
 * `setupState.ts` maps each area to its section, and `sectionOf` looks it up), so the rail is free
 * to be ordered by the owner — driver first — while `get_contract_setup_status` still declares
 * vehicle first. What DOES follow the server's order is any list rendered from `blockers` in the
 * order it arrives: the activation confirm's reason list and the footer's "first blocker · N more".
 *
 * `summary` is deliberately NOT one of them: it is the anchor the activation panel sits at, so a
 * lifecycle blocker (`CONTRACT_NOT_DRAFT`) carries `"section": "summary"` and lands in no section's
 * blocker list — which is what lets all six still read `done` on an activated contract.
 */
export type SetupSectionKey =
    | 'vehicle'
    | 'parties'
    | 'terms'
    | 'deposits'
    | 'insurance'
    | 'agreements';

export type SetupSectionStatus = 'done' | 'todo' | 'optional' | 'blocked';

export interface SetupStatusSection {
    key: SetupSectionKey;
    status: SetupSectionStatus;
    required_total: number;
    required_done: number;
    optional_total: number;
    optional_done: number;
    /** The codes filed against this section, suppression already applied. */
    blockers: string[];
    /** The code of the cross-section prerequisite that stops this section starting at all. */
    blocked_by: string | null;
}

export interface SetupStatusBlocker {
    code: string;
    /** One of the six section keys, or `summary` for the lifecycle arm. */
    section: SetupSectionKey | 'summary';
    field: string;
    message: string;
    /** Always `server` today — the list IS the refusal list, never advice. */
    enforced_by: string;
}

export interface ContractSetupStatus {
    contract_id: number;
    contract_status: string;
    can_activate: boolean;
    /** An ARRAY, not an object: order is the server's own, and jsonb would sort object keys. */
    sections: SetupStatusSection[];
    blockers: SetupStatusBlocker[];
}

/**
 * `get_contract_setup_status` — whether this contract may be activated, and everything in the way.
 *
 * THE AUTHORITATIVE GATE INPUT. Completion used to be derived three times in three vocabularies
 * — `get_preparation_status`, `get_contract_agreements_status` and this page's own rules — and the
 * three disagreed; the loudest symptom was a green Activate button the server then refused, in the
 * whole of the window between the last signature and the finalizer. This function reads the seal
 * state `sign_contract` actually consumes (the legacy `signed_at` + non-empty `agreement_url`
 * pair), so its answer and the server's refusal cannot come apart.
 *
 * CAST BECAUSE THE GENERATED TYPES PREDATE IT. `20260802020000` is live on staging and the types
 * in `integrations/supabase/types.ts` have not been regenerated since, so the client's `rpc`
 * overloads do not know this name. The cast is through `UntypedRpc` rather than the `as any` this
 * app has used elsewhere for the same reason: `any` would also silence the ARGUMENT — and an
 * argument named wrong is the failure mode this codebase has actually shipped, twice, because
 * PostgREST answers a mis-named parameter with a 404 rather than an error anybody reads. Here the
 * name and the shape of what goes IN are still checked, and only the untyped `data` coming back is
 * asserted — once, in the one place the payload is given its real shape.
 */
type UntypedRpc = (
    fn: string,
    args: Record<string, unknown>,
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

export async function fetchContractSetupStatus(
    contractId: number,
): Promise<ContractSetupStatus | null> {
    if (!contractId) return null;
    // THE CAST IS ON THE CLIENT, NOT ON THE METHOD. `SupabaseClient.rpc` reads `this.rest`, so
    // pulling the function off the client (`const rpc = supabase.rpc as …`) — or calling through a
    // cast expression, which detaches the receiver the same way — throws a `TypeError` before a
    // request is made, on every mount, in every environment. Casting the CLIENT keeps the call a
    // member call and still gives `UntypedRpc` the argument shape to check.
    const { data, error } = await (supabase as unknown as { rpc: UntypedRpc }).rpc(
        'get_contract_setup_status',
        { contract_id_param: contractId },
    );
    if (error) throw error;
    return (data ?? null) as ContractSetupStatus | null;
}

/**
 * The contract, its driver's profile and its vehicle's plate — one row, one round trip.
 *
 * The driver's photo and identification documents ride along because the manifest's collapsed
 * "Driver file · N of 6 recorded" line counts them, and a second query for two booleans would be
 * a second key to keep invalidated. The vehicle's colour, year and VIN ride along for the same
 * reason: the assignment block prints the car the agreement will be written over, and the VIN is
 * how an admin checks it is the right one.
 *
 * `overdue_interest_rate` AND `driver_id` ARE COLUMNS OF THIS ROW AND HAVE TO BE ASKED FOR. Both
 * were read by consumers that this select never fetched, and PostgREST simply omits a column
 * nobody names: clause 2.3 printed the last-resort 400 for every contract on the platform —
 * including the 59 written at 300 and the 17 at 0 — and the activation summary listed
 * `contract.overdue_interest_rate` as an unfilled required field on contracts that hold one.
 * `driver_id` is the fallback the snapshot uses when the `drivers` embed is absent.
 *
 * `cash_price` IS THE SAME KIND OF COLUMN AND THE SAME KIND OF HAZARD. It is the Schedule's first
 * money row and it is owner-supplied — never derived from `sold_at`, which is the hire-purchase
 * price — so a select that omitted it would hand the Financials canvas an undefined it would
 * render as an empty field, and an admin who had already typed the price would type it again over
 * the top of itself.
 *
 * `profiles.admin_id` IS SELECTED BECAUSE THE EMBED IS NOT ONE ROW. `profiles.driver_id` is unique
 * only WHERE `admin_id IS NULL`, so an admin who has put their own profile against this driver for
 * testing comes back in the same array — and taking `[0]` printed the admin's name and the admin's
 * date of birth on the driver's setup page (contract 309). `driverProfileOf` does the picking; this
 * select's only job is to hand it the column it picks on.
 *
 * `created_at` IS WHEN SETUP OPENED, and it is the ledger's `days in setup`. It is asked for here
 * rather than counted from a status row for the reason the count would be wrong: a contract that
 * has never been updated has no `contract_updates` row at all, which is the exact population this
 * page exists for.
 */
export async function fetchContractSetup(contractId: number) {
    if (!contractId) return null;
    const { data, error } = await supabase
        .from('contracts')
        .select(`
            id,
            created_at,
            driver_id,
            vehicle_id,
            vehicle_mileage,
            cash_price,
            sold_at,
            management_rate,
            overdue_interest_rate,
            drivers(
                id,
                repayment_tag,
                profiles(id, admin_id, first_name, last_name, birth_date, phone, email, address, photo),
                driver_identification(type, document_url)
            ),
            vehicles(id, make, model, color, manufactured_at, vin, partner_id, vehicle_registrations!vehicles_registration_id_fkey(number))
        `)
        .eq('id', contractId)
        .single();
    if (error) throw error;
    return data;
}

/** `get_preparation_status` — the six items `sign_contract` gates on. */
export async function fetchPreparationStatus(contractId: number): Promise<PreparationItem[] | null> {
    if (!contractId) return null;
    const { data, error } = await supabase.rpc('get_preparation_status', {
        contract_id_param: contractId,
    });
    if (error) throw error;
    return data as PreparationItem[];
}

/**
 * The contract's current status = the LATEST `contract_updates` row.
 *
 * PGRST116 is "no rows", which is a DRAFT that has never been updated — not a failure.
 */
export async function fetchContractStatusForSigning(contractId: number): Promise<string> {
    const { data, error } = await supabase
        .from('contract_updates')
        .select('status')
        .eq('contract_id', contractId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
    if (error && error.code !== 'PGRST116') throw error;
    return data?.status || 'DRAFT';
}

/**
 * The odometer reading at handover — this contract's column, not a vehicle one.
 *
 * IT IS AN OPERATIONAL RECORD, NOT A MERGE FIELD. No instrument prints it: the v1 Schedule
 * identifies the Vehicle by make and model, year, colour, registration and condition. What it is
 * for is the handover baseline — the figure a later argument about wear, mileage or valuation is
 * settled against — which is why activation is held until it is recorded.
 *
 * MOVED OUT OF `MileageForm`, whose only remaining job was to be a right-hand sheet containing one
 * number. The validation is that sheet's, kept word for word — and it stays a refusal rather than
 * a coerced zero, because zero is a reading a brand-new car legitimately has.
 */
export async function saveVehicleMileage(contractId: number, mileage: string): Promise<void> {
    const value = parseFloat(mileage);
    if (isNaN(value) || value < 0) throw new Error('Please enter a valid mileage');

    const { error } = await supabase
        .from('contracts')
        .update({ vehicle_mileage: value })
        .eq('id', contractId);
    if (error) throw error;
}
